// Import react elements
import { useContext, useEffect, useRef, useState } from "react";

// Import context function
import { AuthContext } from "global/context/AuthContext";
import { UserContext } from "global/context/UserContext";
import { AccountLastUpdatedContext } from "global/context/AccountLastUpdatedContext";

// Import hooks
import usePageTitle from "global/hooks/usePageTitle";

// Import components
import Transactions from "./components/Transactions";
import { Grid, GridItem } from "global/components/Grid";

// Import utilities
import { FormatCurrency } from "global/utilities/ConvertCurrency";

// Import funtions from other pages
import { loadDashboardData } from "pages/dashboard/DashboardPage";

// Import css
import "./BalanceAndTransactionsPage.css";
import LoadingDotsAnimation from "global/components/loading_ui/LoadingDotsAnimation";





// BalanceAndTransactionsPage.js
const BalanceAndTransactions = () => {


    // Set title inside the page
    usePageTitle("Balance");



    // Get the token from AuthContext
    const { token, authReady } = useContext(AuthContext);

    // Get the account data from UserContext
    const { userData } = useContext(UserContext);

    // Get the global account update markers
    const { lastUpdated } = useContext(AccountLastUpdatedContext);

    // Get the transactions marker
    const transactionsLastUpdated = lastUpdated?.transactions_last_updated || null;

    // Store the marker that was current when the balance was last loaded
    const loadedTransactionsLastUpdatedRef = useRef(null);

    // Prevent overlapping automatic balance refreshes
    const autoRefreshInProgressRef = useRef(false);

    // Store dashboard data
    const [dashboardData, setDashboardData] = useState(null);

    // Track loading state
    const [loading, setLoading] = useState(true);

    // Track loading errors
    const [errorMessage, setErrorMessage] = useState("");



    // Load dashboard data when auth becomes available
    useEffect(() => {

        // Return early if auth is not ready or token is missing
        if (!authReady || !token) {

            return;

        }

        // Trigger data load
        loadDashboardData(token, setDashboardData, setLoading, setErrorMessage);

    }, [token, authReady]);



    // Refresh balance when the global transactions marker changes
    useEffect(() => {

        // Only refresh when auth is ready
        if (!authReady || !token) { return; }

        // Ignore empty/default markers
        if (!transactionsLastUpdated || transactionsLastUpdated === "0001-01-01 00:00:00") { return; }

        // If this is the first marker seen, store it without refreshing
        if (!loadedTransactionsLastUpdatedRef.current) {
            loadedTransactionsLastUpdatedRef.current = transactionsLastUpdated;
            return;
        }

        // Stop if the marker has not changed since the last balance load
        if (transactionsLastUpdated === loadedTransactionsLastUpdatedRef.current) { return; }

        // Stop if an automatic refresh is already running
        if (autoRefreshInProgressRef.current) { return; }

        // Mark the automatic refresh as running
        autoRefreshInProgressRef.current = true;

        // Reload dashboard data quietly in the background
        loadDashboardData(token, setDashboardData, () => {}, () => {}).finally(() => {

            // Store the marker after the refresh
            loadedTransactionsLastUpdatedRef.current = transactionsLastUpdated;

            // Mark the automatic refresh as finished
            autoRefreshInProgressRef.current = false;

        });

    }, [authReady, token, transactionsLastUpdated]);




    // Total unsettled account balance
    const balance = dashboardData?.balance ?? null;

    // Account balance currently available to the user
    const availableBalance = dashboardData?.available_balance ?? null;



    return (

        <>

            <Grid>

                {/* Balance Card */}
                <GridItem className="dashboard-stat-card">

                    <p className="dashboard-stat-card__label">Balance</p>

                    {loading && balance === null ? 
                    
                        <>
                        
                            <p>Loading<LoadingDotsAnimation /></p>

                        </>

                        :

                        errorMessage ?

                            <>

                                <p className="dashboard-stat-card__value">N/A</p>
                                <p className="dashboard-stat-card__meta warning-message">{errorMessage}</p>
                            
                            </>

                            :

                            <>
                            
                                <p className="dashboard-stat-card__value">{FormatCurrency(userData?.currency, balance, true)}</p>
                                <p className="dashboard-stat-card__meta">Available: {FormatCurrency(userData?.currency, availableBalance, true)}</p>
                            
                            </>

                    }
                    
                </GridItem>

            </Grid>

            {Transactions(token, authReady)}

        </>

    );

};

export default BalanceAndTransactions;